import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { POSTS_API } from "@/lib/apis";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { TrashIcon } from "lucide-react";
import toast from "react-hot-toast";

function DeletePostAlertDialog({
	postSlug,
	postTitle,
	communitySlug,
	channelSlug,
}: {
	postSlug: string;
	postTitle: string;
	communitySlug: string | undefined;
	channelSlug: string;
}) {
	const queryClient = useQueryClient();

	const handleDeletePost = async () => {
		const url = new URL(`${POSTS_API}/${postSlug}`);

		await fetch(url, {
			method: "DELETE",
		});
	};

	const deletePostMutation = useMutation<void, Error>({
		mutationFn: handleDeletePost,
		onSuccess: () => {
			toast.success("Post deleted successfully");
			return queryClient.invalidateQueries({ queryKey: ["posts", communitySlug, channelSlug] });
		},
		onError: (error) => {
			toast.error(error.message);
		},
	});

	return (
		<AlertDialog>
			<AlertDialogTrigger asChild>
				<button
					type="button"
					className="flex items-center justify-center gap-1 rounded-md border border-gray-300 p-2 text-sm font-semibold text-red-500 transition hover:bg-gray-50"
				>
					<TrashIcon className="h-4 w-4" />

					<span className="sr-only">Delete post with title - {postTitle}</span>
				</button>
			</AlertDialogTrigger>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>Are you sure you want to delete this post?</AlertDialogTitle>
					<AlertDialogDescription>
						Deleting post with title <span className="font-semibold">{`"${postTitle}"`}</span>. This
						action cannot be undone.
					</AlertDialogDescription>
				</AlertDialogHeader>

				<AlertDialogFooter>
					<AlertDialogCancel>Cancel</AlertDialogCancel>
					<AlertDialogAction
						disabled={deletePostMutation.isLoading}
						onClick={() => deletePostMutation.mutate()}
					>
						<span className="text-red-500">Confirm Delete</span>
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
}

export default DeletePostAlertDialog;
